import type { ReactNode } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '../utils/cn';

interface KpiCardProps {
  title: string;
  subtitle?: string;
  value: string | number;
  trend?: { direction: 'up' | 'down' | 'neutral'; value: number; label?: string };
  icon?: ReactNode;
  visualization?: ReactNode;
  onClick?: () => void;
  className?: string;
}

export default function KpiCard({ title, subtitle, value, trend, icon, visualization, onClick, className }: KpiCardProps) {
  const TrendIcon = trend?.direction === 'up' ? TrendingUp : trend?.direction === 'down' ? TrendingDown : Minus;
  const trendColor = trend?.direction === 'up' ? 'text-success' : trend?.direction === 'down' ? 'text-danger' : 'text-content-muted';

  return (
    <div
      onClick={onClick}
      className={cn(
        'rounded-xl border border-border bg-surface-card p-4 transition-colors',
        onClick && 'cursor-pointer hover:bg-surface-card-hover',
        className
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-content-muted uppercase tracking-wide truncate">{title}</p>
          {subtitle && <p className="text-[11px] text-content-muted mt-0.5">{subtitle}</p>}
          <p className="mt-2 text-2xl font-bold text-content-primary">{value}</p>
          {trend && (
            <div className={cn('flex items-center gap-1 mt-1', trendColor)}>
              <TrendIcon size={14} />
              <span className="text-xs font-medium">{trend.value}%</span>
              {trend.label && <span className="text-xs text-content-muted">{trend.label}</span>}
            </div>
          )}
        </div>
        {icon && <div className="flex-shrink-0 ml-3">{icon}</div>}
      </div>
      {visualization && <div className="mt-3">{visualization}</div>}
    </div>
  );
}
